import React, { useMemo, useState } from 'react';

import { Money } from '../../../graphql/schema';
import { convertToMoneyWithDefault } from '../../shared/helpers/utils';

export type ProjectionInputs = {
  majorPurchase?: { adHocInflow: Money; monthlyRecurringDeposits: Money };
  emergencyFund?: { adHocInflow: Money; monthlyRecurringDeposits: Money };
  retirementGrowth?: {
    annualRecurringDeposits: Money;
    retirementAge: number;
    retirementZip: string;
    targetMonthlySpendingAmount: Money;
  };
  retirementIncome?: { adHocInflow: Money; monthlyRecurringWithdrawals: Money };
};

type ForecasterInputsUpdate = {
  majorPurchaseProjectionInput: (overrides: NonNullable<ProjectionInputs['majorPurchase']>) => void;
  emergencyFundProjectionInput: (overrides: NonNullable<ProjectionInputs['emergencyFund']>) => void;
  retirementGrowthProjectionInput: (overrides: NonNullable<ProjectionInputs['retirementGrowth']>) => void;
  retirementIncomeProjectionInput: (overrides: NonNullable<ProjectionInputs['retirementIncome']>) => void;
};

export const ForecasterPurposeInputsContext = React.createContext<{
  projectionQueryInputs: ProjectionInputs;
  update: ForecasterInputsUpdate;
} | null>(null);

export const ForecasterPurposeInputsProvider: React.FC<React.PropsWithChildren<unknown>> = ({ children }) => {
  const [projectionQueryInputs, setProjectionQueryInputs] = useState<ProjectionInputs>({});

  const value = useMemo(() => {
    if (!projectionQueryInputs) return null;

    const update: ForecasterInputsUpdate = {
      majorPurchaseProjectionInput: input =>
        setProjectionQueryInputs({
          majorPurchase: {
            adHocInflow: convertToMoneyWithDefault(input.adHocInflow),
            monthlyRecurringDeposits: convertToMoneyWithDefault(input.monthlyRecurringDeposits),
          },
        }),
      emergencyFundProjectionInput: input =>
        setProjectionQueryInputs({
          emergencyFund: {
            adHocInflow: convertToMoneyWithDefault(input.adHocInflow),
            monthlyRecurringDeposits: convertToMoneyWithDefault(input.monthlyRecurringDeposits),
          },
        }),
      retirementGrowthProjectionInput: input =>
        setProjectionQueryInputs({
          retirementGrowth: {
            annualRecurringDeposits: convertToMoneyWithDefault(input.annualRecurringDeposits),
            retirementAge: input.retirementAge,
            retirementZip: input.retirementZip,
            targetMonthlySpendingAmount: convertToMoneyWithDefault(input.targetMonthlySpendingAmount),
          },
        }),
      retirementIncomeProjectionInput: input =>
        setProjectionQueryInputs({
          retirementIncome: {
            adHocInflow: convertToMoneyWithDefault(input.adHocInflow),
            monthlyRecurringWithdrawals: convertToMoneyWithDefault(input.monthlyRecurringWithdrawals),
          },
        }),
    };

    return { projectionQueryInputs, update };
  }, [projectionQueryInputs, setProjectionQueryInputs]);

  return <ForecasterPurposeInputsContext.Provider value={value}>{children}</ForecasterPurposeInputsContext.Provider>;
};
